import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  Gift,
  KeyRound,
  LogIn,
  MailCheck,
  Power,
  UserCheck,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import { ApiError } from '@/shared/api/client';
import {
  type AdminUser,
  useAdminUser,
  useBanUser,
  useDeactivateUser,
  useReactivateUser,
  useResendVerification,
  useResetUserPassword,
  useUnbanUser,
  useVerifyUserEmail,
} from '@/shared/api/admin-users';
import { Badge, Button, ErrorBanner, Muted } from '@/shared/ui/kit';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

import { AccountCard, ActivityCard, AdminHistoryCard, PlanCard, TeamsCard } from './user-detail-cards';

type DialogKind = 'ban' | 'unban' | 'deactivate' | 'reactivate' | 'verify' | 'resend' | 'reset';

export function UserDetailPage({ id }: { id: string }) {
  const { data: user, isLoading, error, refetch } = useAdminUser(id);

  if (isLoading) {
    return (
      <AdminLayout>
        <BackLink />
        <Muted>Loading…</Muted>
      </AdminLayout>
    );
  }
  if (error || !user) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <AdminLayout>
        <BackLink />
        {notFound ? (
          <Muted>No user with id {id}.</Muted>
        ) : (
          <ErrorBanner error={error ?? new Error('User not available')} onRetry={() => void refetch()} />
        )}
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <BackLink />
      <UserHeader user={user} />
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <AccountCard user={user} />
        <ActivityCard id={user.id} />
        <PlanCard user={user} />
        <TeamsCard id={user.id} fallbackCount={user.teamCount} />
        <div className="md:col-span-2 xl:col-span-2">
          <AdminHistoryCard id={user.id} />
        </div>
      </section>
    </AdminLayout>
  );
}

function BackLink() {
  return (
    <Link to="/users" className="mb-3 inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-neutral-200">
      <ArrowLeft size={14} /> All users
    </Link>
  );
}

function UserHeader({ user }: { user: AdminUser }) {
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const ban = useBanUser();
  const unban = useUnbanUser();
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const verify = useVerifyUserEmail();
  const resend = useResendVerification();
  const reset = useResetUserPassword();

  const unverified = !user.emailVerifiedAt;
  const protectedAccount = user.isSuperadmin;

  const DIALOGS: Record<
    DialogKind,
    {
      title: string;
      description: string;
      confirm: string;
      tone: 'default' | 'danger';
      minLength: number;
      run: (reason?: string) => Promise<unknown>;
      done: string;
      pending: boolean;
    }
  > = {
    ban: {
      title: `Ban ${user.email}?`,
      description:
        'Signs them out everywhere, revokes access tokens and blocks sign-in until unbanned. Use for abuse; for a closed account use Deactivate.',
      confirm: 'Ban user',
      tone: 'danger',
      minLength: 10,
      run: (reason) => ban.mutateAsync({ id: user.id, reason: reason ?? '' }),
      done: 'User banned',
      pending: ban.isPending,
    },
    unban: {
      title: `Unban ${user.email}?`,
      description: 'They can sign in again and reappear in team rosters.',
      confirm: 'Unban',
      tone: 'default',
      minLength: 0,
      run: (reason) => unban.mutateAsync({ id: user.id, reason }),
      done: 'User unbanned',
      pending: unban.isPending,
    },
    deactivate: {
      title: `Deactivate ${user.email}?`,
      description:
        'Signs them out everywhere and blocks sign-in. Data and team memberships stay. Reversible with Reactivate.',
      confirm: 'Deactivate',
      tone: 'danger',
      minLength: 10,
      run: (reason) => deactivate.mutateAsync({ id: user.id, reason }),
      done: 'User deactivated',
      pending: deactivate.isPending,
    },
    reactivate: {
      title: `Reactivate ${user.email}?`,
      description: 'Sign-in is allowed again. Sessions are not restored — they sign in fresh.',
      confirm: 'Reactivate',
      tone: 'default',
      minLength: 0,
      run: (reason) => reactivate.mutateAsync({ id: user.id, reason }),
      done: 'User reactivated',
      pending: reactivate.isPending,
    },
    verify: {
      title: 'Mark email as verified?',
      description: `Treats ${user.email} as confirmed without the link. Only do this when you know the person owns the address.`,
      confirm: 'Mark verified',
      tone: 'default',
      minLength: 0,
      run: (reason) => verify.mutateAsync({ id: user.id, reason }),
      done: 'Email marked as verified',
      pending: verify.isPending,
    },
    resend: {
      title: 'Resend verification email?',
      description: `A new confirmation link (valid 24h) goes to ${user.email}. Any earlier link stops working.`,
      confirm: 'Send email',
      tone: 'default',
      minLength: 0,
      run: (reason) => resend.mutateAsync({ id: user.id, reason }),
      done: 'Verification email queued',
      pending: resend.isPending,
    },
    reset: {
      title: 'Send password reset email?',
      description: `A reset link (valid 1 hour) goes to ${user.email}. Their current password keeps working until they use it.`,
      confirm: 'Send email',
      tone: 'default',
      minLength: 0,
      run: (reason) => reset.mutateAsync({ id: user.id, reason }),
      done: 'Password reset email queued',
      pending: reset.isPending,
    },
  };
  const d = dialog ? DIALOGS[dialog] : null;

  return (
    <header className="mb-5">
      <div className="mb-3 min-w-0">
        <h1 className="truncate text-2xl font-semibold text-neutral-100">{user.name}</h1>
        <p className="break-all text-sm text-neutral-400">
          {user.email} · @{user.username}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {user.isBanned ? (
            <Badge tone="danger">banned</Badge>
          ) : user.isActive ? (
            <Badge tone="ok">active</Badge>
          ) : (
            <Badge tone="warn">deactivated</Badge>
          )}
          {unverified ? <Badge tone="warn">email not verified</Badge> : <Badge tone="ok">verified</Badge>}
          {user.twoFactorEnabled ? <Badge tone="info">2FA on</Badge> : <Badge tone="neutral">2FA off</Badge>}
          {user.isSuperadmin ? <Badge tone="brand">superadmin</Badge> : null}
          {user.personalPlanCode ? <Badge tone="brand">{user.personalPlanCode}</Badge> : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" role="toolbar" aria-label="User actions">
        <Link
          to="/subscriptions/grant"
          search={{ customerType: 'user' as const, customerId: user.id }}
          className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white ring-1 ring-orange-500/40 hover:bg-orange-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
        >
          <Gift size={14} /> Grant plan
        </Link>
        {unverified ? (
          <>
            <Button icon={<CheckCircle2 size={14} />} onClick={() => setDialog('verify')}>
              Mark verified
            </Button>
            <Button icon={<MailCheck size={14} />} onClick={() => setDialog('resend')} disabled={!user.isActive || user.isBanned}>
              Resend verification
            </Button>
          </>
        ) : null}
        <Button icon={<KeyRound size={14} />} onClick={() => setDialog('reset')} disabled={!user.isActive}>
          Reset password
        </Button>
        {user.isActive ? (
          <Button
            icon={<Power size={14} />}
            onClick={() => setDialog('deactivate')}
            disabled={protectedAccount}
            title={protectedAccount ? 'Superadmins are managed via the CLI' : undefined}
          >
            Deactivate
          </Button>
        ) : (
          <Button icon={<Power size={14} />} tone="ok" onClick={() => setDialog('reactivate')}>
            Reactivate
          </Button>
        )}
        {user.isBanned ? (
          <Button icon={<UserCheck size={14} />} tone="ok" onClick={() => setDialog('unban')}>
            Unban
          </Button>
        ) : (
          <Button
            icon={<Ban size={14} />}
            tone="danger"
            onClick={() => setDialog('ban')}
            disabled={protectedAccount}
            title={protectedAccount ? 'Superadmins are managed via the CLI' : undefined}
          >
            Ban
          </Button>
        )}
        <Button
          icon={<LogIn size={14} />}
          disabled
          title="Log in as this user needs a hand-off into the customer app, which is not built yet. The API endpoint exists and is audited."
        >
          Log in as user
        </Button>
      </div>

      {d ? (
        <ReasonDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          title={d.title}
          description={d.description}
          confirmLabel={d.confirm}
          tone={d.tone}
          minLength={d.minLength}
          reasonLabel={d.minLength > 0 ? 'Reason' : 'Note'}
          placeholder={d.minLength > 0 ? 'Briefly explain why.' : 'Optional — e.g. support ticket or chat link'}
          pending={d.pending}
          onConfirm={async (reason) => {
            await d.run(reason || undefined);
            toast.success(d.done);
            setDialog(null);
          }}
        />
      ) : null}
    </header>
  );
}
